<template>
  <div class="flex flex-col items-center">
    <div class="relative flex flex-col items-center w-full mb-128 md:mb-256">
      <div
        class="relative flex items-center w-full md:w-auto max-w-[40rem] sm:max-w-[1480px] pt-96 md:pt-160 px-20 sm:px-40 xl:px-0 z-10"
      >
        <div class="flex flex-col lg:mb-40">
          <h1
            v-html="$t('200.hero.title')"
            class="text-[38px] sm:text-[64px] lg:text-[68px] xl:text-[80px] max-w-[680px] font-special leading-[1.1] fade-in-move-up mb-16 sm:mb-20"
          ></h1>

          <div class="max-w-[280px] md:max-w-[420px] fade-in-move-up-2">
            {{ $t("200.hero.description") }}
          </div>

          <div
            class="flex flex-col md:flex-row items-center gap-20 w-fit mt-40"
          >
            <SharedLink url="/referrals" hash="#how" class="fade-in-move-up-3">
              <UiButton>
                {{ $t("200.hero.skip") }}
              </UiButton>
            </SharedLink>

            <SharedLink
              url="/referrals"
              hash="#who"
              class="underline text-[17px] fade-in-move-up-4"
            >
              {{ $t("200.hero.cta") }}
            </SharedLink>
          </div>
        </div>

        <img
          :src="hero"
          class="hidden md:block shrink-0 rounded-lg object-cover md:h-[400px] lg:h-512 md:w-[400px] lg:w-512 rotate-45 -z-10"
        />
      </div>

      <img
        :src="hero"
        class="absolute bottom-0 -right-80 md:hidden rounded-lg object-cover h-256 sm:h-[320px] w-256 sm:w-[320px] rotate-45"
      />
    </div>

    <div
      id="who"
      class="relative flex flex-col items-center w-full pt-160 bg-[#e3d2be]"
    >
      <div
        class="relative flex flex-col sm:items-center w-full md:max-w-[calc(90vw)] xl:max-w-[1480px] h-full px-20 sm:px-40 xl:px-0"
      >
        <h2 class="max-w-256 sm:max-w-none">{{ $t("200.know.title") }}</h2>

        <div
          class="grid sm:grid-cols-3 gap-64 sm:gap-16 md:gap-80 md:mt-64 w-full"
        >
          <div
            v-for="group in [
              {
                src: New,
                description: $t('200.know.rebranding'),
              },
              {
                src: Young,
                description: $t('200.know.founding'),
              },
              {
                src: Old,
                description: $t('200.know.first_time'),
              },
            ]"
            class="relative flex flex-col items-center gap-16 sm:gap-24 w-full rounded-xl"
          >
            <img
              :src="group.src"
              class="shrink-0 rounded-lg h-256 lg:h-[360px] object-cover w-full"
            />

            <div class="w-full">
              {{ group.description }}
            </div>
          </div>
        </div>

        <SharedLink url="/referrals" hash="#how" class="w-fit mt-80">
          <UiButton>
            {{ $t("200.offer.continue") }}
          </UiButton>
        </SharedLink>

        <div class="relative mt-128 sm:mt-160 md:mt-256">
          <div
            class="absolute right-0 sm:-right-40 -top-40 sm:top-0 md:-top-40 max-w-160 sm:max-w-[190px] text-right mb-40"
          >
            {{ $t("200.know.you") }}
          </div>

          <img
            :src="maybeYou"
            height="800"
            width="800"
            class="shrink-0 rounded-lg w-[480px]"
          />
        </div>
      </div>
    </div>

    <div class="flex flex-col items-center w-full">
      <div
        class="flex flex-col items-center w-full py-80 sm:py-128 lg:py-160 px-20 sm:px-40 xl:px-0"
      >
        <div
          class="flex flex-col items-center gap-16 w-full max-w-full md:max-w-[calc(90vw)] xl:max-w-[1480px]"
        >
          <div
            class="flex flex-col md:grid md:grid-cols-2 gap-80 xl:gap-128 w-full"
          >
            <div
              v-for="(option, i) in [
                {
                  src: knowSomeone,
                  title: $t('200.offer.friend.title'),
                  description: $t('200.offer.friend.description'),
                },
                {
                  src: knowYou,
                  title: $t('200.offer.you.title'),
                  description: $t('200.offer.you.description'),
                },
              ]"
              class="w-full max-w-full"
            >
              <div class="flex flex-col w-full max-w-full">
                <h2>{{ option.title }}</h2>

                <img
                  :src="option.src"
                  class="shrink-0 rounded-lg h-256 sm:h-[320px] xl:h-512 object-cover object-top sm:w-full"
                />

                <div v-html="option.description" class="mt-24"></div>
              </div>

              <div v-if="i === 0" class="mt-24 sm:mt-40">
                <SharedLink url="/referrals" hash="#how" class="w-fit">
                  <UiButton>
                    {{ $t("200.offer.continue") }}
                  </UiButton>
                </SharedLink>
              </div>

              <div
                v-else-if="i === 1"
                class="flex flex-col lg:grid lg:grid-cols-2 gap-16 text-left mt-24 sm:mt-40"
              >
                <UiClickBox
                  url="/"
                  hash="#pricing"
                  :label="$t('200.offer.packages.title')"
                  :description="$t('200.offer.packages.description')"
                  class="bg-[#e3d2be]"
                />

                <UiClickBox
                  url="/"
                  hash="#services"
                  :label="$t('200.offer.benefits.title')"
                  :description="$t('200.offer.benefits.description')"
                  class="bg-[#e3d2be]"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-col items-center w-full bg-[#e3d2be]">
      <div
        id="how"
        class="flex flex-col items-center w-full py-80 sm:py-128 lg:py-160 px-20 sm:px-40 xl:px-0"
      >
        <div class="flex flex-col items-center gap-16 w-full max-w-[40rem]">
          <h2>{{ $t("200.howto.title") }}</h2>

          <ol class="flex flex-col gap-20 w-full">
            <li class="flex gap-12 w-full mb-12">
              <div>1.</div>
              <div class="flex flex-col gap-8">
                <div>
                  {{ $t("200.howto.recommendation.description") }}
                </div>

                <div>
                  {{ $t("200.howto.recommendation.website_link") }}
                </div>

                <div
                  class="relative flex items-center gap-16 bg-[#f3e9dc] px-20 py-12 rounded-md w-fit transition-all cursor-pointer"
                  @click="copyWebsiteLink()"
                >
                  <div>hyperfocused.agency</div>

                  <IconsIconCopy class="h-24 w-24" />

                  <div
                    v-if="showCopyHintWebsiteLink"
                    class="absolute bottom-64 right-0 px-12 py-8 rounded-md bg-dark text-beige"
                  >
                    {{ $t("200.howto.recommendation.copy_success") }}
                  </div>
                </div>

                <div>
                  {{ $t("200.howto.recommendation.calendar_link") }}
                </div>

                <div
                  class="relative flex items-center gap-16 bg-[#f3e9dc] px-20 py-12 rounded-md w-fit transition-all cursor-pointer"
                  @click="copyMeetingLink()"
                >
                  <div>hyperfocused.agency/contact</div>

                  <IconsIconCopy class="h-24 w-24" />

                  <div
                    v-if="showCopyHintMeetingLink"
                    class="absolute bottom-64 right-0 px-12 py-8 rounded-md bg-dark text-beige"
                  >
                    {{ $t("200.howto.recommendation.copy_success") }}
                  </div>
                </div>
              </div>
            </li>

            <li class="flex gap-12 mb-12">
              <div>2.</div>
              <div class="flex flex-col gap-8">
                <div>
                  {{ $t("200.howto.mail.description") }}
                </div>

                <SharedLink
                  :url="`mailto:200@hyperfocused.agency?${$t(
                    '200.howto.mail.prefill'
                  )}`"
                  class="w-fit"
                >
                  <UiButton size="small">
                    {{ $t("200.howto.mail.cta") }}</UiButton
                  >
                </SharedLink>
              </div>
            </li>

            <li class="flex gap-12">
              <div>3.</div>

              <div>{{ $t("200.howto.success") }}</div>
            </li>
          </ol>

          <div class="mt-40">
            {{ $t("200.howto.disclaimer") }}
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-col items-center w-full">
      <div
        class="flex flex-col items-center w-full py-80 sm:py-128 lg:py-160 px-20 sm:px-40 xl:px-0"
      >
        <div class="flex flex-col gap-16 w-full max-w-[40rem]">
          <h2 v-html="$t('200.conditions.title')"></h2>

          <ol class="flex flex-col gap-20 w-full">
            <li
              v-for="(item, i) in $tm('200.conditions.rules')"
              :key="item"
              class="flex gap-12"
            >
              <div>({{ i + 1 }})</div>

              <div>{{ item }}</div>
            </li>
          </ol>
        </div>
      </div>
    </div>

    <div
      class="relative flex flex-col items-center w-full mt-80 sm:mt-128 px-4 sm:px-24 mb-4 sm:mb-24 z-20"
    >
      <div
        class="relative flex flex-col bg-[#829C80] text-beige px-32 pt-48 sm:py-40 lg:py-80 sm:px-40 lg:px-80 rounded-b-[78px] rounded-t-xl sm:rounded-b-[72px] sm:rounded-t-[72px] w-full max-w-[calc(55rem-44px)] overflow-hidden"
      >
        <div class="relative">
          <h2 class="max-w-[512px] xl:max-w-[680px]">
            {{ $t("200.share.title") }}
          </h2>

          <UiButton
            variant="secondary"
            class="hidden sm:flex w-fit"
            @click="shareLinkToOffer()"
            >{{ $t("200.share.cta") }}</UiButton
          >
        </div>

        <div class="sm:hidden absolute bottom-32 left-32 right-32">
          <UiButton
            variant="secondary"
            class="w-full"
            @click="shareLinkToOffer()"
            >{{ $t("200.share.cta") }}</UiButton
          >
        </div>

        <img
          :src="sharePromotion"
          class="w-full h-auto aspect-video object-contain md:-mt-40 mb-128 sm:mb-0"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import knowSomeone from "~/assets/img/referrals/know-someone.webp";
import knowYou from "~/assets/img/referrals/know-you.webp";
import maybeYou from "~/assets/img/referrals/maybe-you.webp";
import New from "~/assets/img/referrals/question-new.webp";
import Old from "~/assets/img/referrals/question-old.webp";
import Young from "~/assets/img/referrals/question-young.webp";
import hero from "~/assets/img/referrals/referrals-hero.webp";
import sharePromotion from "~/assets/img/referrals/share-promotion.webp";

import { setHead } from "~/lib/head";

setHead("referrals");

const showCopyHintWebsiteLink = ref(false);
const showCopyHintMeetingLink = ref(false);
async function copyWebsiteLink() {
  await copyToClipboard("https://hyperfocused.agency", showCopyHintWebsiteLink);
}
async function copyMeetingLink() {
  await copyToClipboard(
    "https://hyperfocused.agency/contact",
    showCopyHintMeetingLink
  );
}
async function copyToClipboard(text: string, hintRef: Ref<boolean>) {
  await navigator.clipboard.writeText(text);

  hintRef.value = true;
  setTimeout(() => {
    hintRef.value = false;
  }, 1500);
}

async function shareLinkToOffer() {
  try {
    await navigator.share({
      title: "",
      text: "",
      url: "https://hyperfocused.agency/referrals",
    });
  } catch (error) {
    console.log("Error sharing:", error);
  }
}
</script>
